import './FilesList.scss';

import { Checkbox } from '@fluentui/react-components';
import { MoreVerticalFilled } from '@fluentui/react-icons';
import { Button } from '../../Button/Button';
import { Popup } from '../../Popup/Popup';
import { FileItem } from '@mnspro-cloud/services';
import cn from 'classnames';
import type { TFunction } from 'i18next';
import { get } from 'lodash';
import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatBytes } from '../../FileUtils/formatBytes';
import { isOfficeFile, isPreviewableImage } from '../Files.utils';
import {
  canBeOpened,
  canDownloadAsZip,
  getFileListRowIcon,
  isDownloadableAsPdf,
  isFolder,
  isRecentFile,
  isSelectable,
} from './FilesList.utils';

interface FilesListRowProps {
  file: FileItem;
  onFolderClick: (folder: FileItem) => void;
  isSelected: boolean;
  onSelectionChange: (next: boolean) => void;
  allowFolderSelection: boolean;
  allowFileSelection?: boolean;
  onShareFileClick?: ((file: FileItem) => void) | null;
  onDownloadAsPdfClick?: ((file: FileItem) => void) | null;
  onOpenFileClick?: ((file: FileItem) => void) | null;
  onDownloadFileClick?: ((file: FileItem) => void) | null;
  onDownloadZipClick?: ((files: FileItem[]) => void) | null;
  onPreviewFileClick?: ((file: FileItem) => void) | null;
  onMoveFileClick?: ((file: FileItem[]) => void) | null;
  onCopyFileClick?: ((file: FileItem[]) => void) | null;
  onDeleteFileClick?: ((file: FileItem) => void) | null;
  onRenameFileClick?: ((file: FileItem) => void) | null;
}

const recentFileIndicator = (t: TFunction) => {
  return (
    <div
      className="recentlyUploaded"
      aria-label={t('files:fileslist:recently_uploaded', { defaultValue: 'Kürzlich hochgeladen' })}
    >
      <svg width="7" height="10" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <g id="Layer_1">
          <line
            stroke="#3e40a7"
            id="svg_1"
            y2="4.46875"
            x2="6.46873"
            y1="1.03127"
            x1="6.46873"
            fill="none"
          />
          <line
            transform="rotate(-45 3.03125 4.125)"
            stroke="#3e40a7"
            id="svg_3"
            y2="5.84375"
            x2="3.03125"
            y1="2.40627"
            x1="3.03125"
            fill="none"
          />
          <line
            transform="rotate(90 1.78126 7.74998)"
            stroke="#3e40a7"
            id="svg_4"
            y2="9.46872"
            x2="1.78126"
            y1="6.03124"
            x1="1.78126"
            fill="none"
          />
        </g>
      </svg>
    </div>
  );
};

/**
 * Single row representing a single file
 */
export const FilesListRow: React.FC<FilesListRowProps> = ({
  file,
  onFolderClick,
  isSelected,
  onSelectionChange,
  allowFolderSelection,
  allowFileSelection = true,
  onShareFileClick = null,
  onDownloadAsPdfClick = null,
  onOpenFileClick = null,
  onDownloadFileClick = null,
  onDownloadZipClick = null,
  onPreviewFileClick = null,
  onMoveFileClick = null,
  onCopyFileClick = null,
  onDeleteFileClick = null,
  onRenameFileClick = null,
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleClick = () => {
    if (file.folder) {
      onFolderClick(file);
    } else if (canBeOpened(file) && isOfficeFile(file.name)) {
      window.open(file?.webUrl as string, '_blank');
    } else if (onPreviewFileClick && isPreviewableImage(file)) {
      onPreviewFileClick(file);
    } else if (onDownloadFileClick) {
      onDownloadFileClick(file);
    }
  };

  const handleCheckChange = () => {
    onSelectionChange(!isSelected);
  };

  const contextMenuItems = useMemo(() => {
    const menuItems = [];

    const isOpenInSharePointAvailable = get(file, 'webUrl', false);

    const runAndClose = (action: () => void) => () => {
      action();
      setMenuOpen(false);
    };

    const onOpenInSharePoint = () => {
      const webUrl = get(file, 'webUrl', false);

      if (webUrl) {
        window.open(webUrl, '_blank');
      }
    };

    if (onOpenFileClick !== null && canBeOpened(file)) {
      menuItems.push(
        <Button
          aria-label={t('open_file_label', {
            defaultValue: 'Öffnen: Öffnet Datei in neuem Tab',
          })}
          className="fileListRowContextMenuBtn"
          key="btnOpen"
          appearance="transparent"
          onClick={runAndClose(() => onOpenFileClick(file))}
        >
          {t('open', { defaultValue: 'Öffnen' })}
        </Button>,
      );
    }

    if (isOpenInSharePointAvailable && canBeOpened(file) && isFolder(file)) {
      menuItems.push(
        <Button
          className="fileListRowContextMenuBtn"
          key="btnOpenInSharePoint"
          appearance="transparent"
          onClick={runAndClose(onOpenInSharePoint)}
        >
          {t('in_sharepoint', { defaultValue: 'In Sharepoint' })}
        </Button>,
      );
    }

    if (onPreviewFileClick !== null && isPreviewableImage(file)) {
      menuItems.push(
        <Button
          aria-label={t('files:fileslist:preview_file_label', {
            defaultValue: 'Vorschau: Öffnet eine Vorschau der Datei',
          })}
          className="fileListRowContextMenuBtn"
          key="btnPreview"
          appearance="transparent"
          onClick={runAndClose(() => onPreviewFileClick(file))}
        >
          {t('files:fileslist:preview', { defaultValue: 'Vorschau' })}
        </Button>,
      );
    }

    if (onDownloadFileClick !== null && !isFolder(file)) {
      menuItems.push(
        <Button
          aria-label={t('download_file_label', {
            defaultValue: 'Herunterladen: Lädt ausgewählte Datei herunter',
          })}
          className="fileListRowContextMenuBtn"
          key="btnDownload"
          appearance="transparent"
          onClick={runAndClose(() => onDownloadFileClick(file))}
        >
          {t('download', { defaultValue: 'Herunterladen' })}
        </Button>,
      );
    }

    if (
      onDownloadZipClick !== null &&
      isFolder(file) &&
      canDownloadAsZip([file])
    ) {
      menuItems.push(
        <Button
          aria-label={t('files:fileslist:download_folder_label', {
            defaultValue: 'Herunterladen: Lädt den Ordner als ZIP-Archiv herunter',
          })}
          className="fileListRowContextMenuBtn"
          key="btnDownloadZip"
          appearance="transparent"
          onClick={runAndClose(() => onDownloadZipClick([file]))}
        >
          {t('download', { defaultValue: 'Herunterladen' })}
        </Button>,
      );
    }

    if (onMoveFileClick !== null) {
      menuItems.push(
        <Button
          aria-label={t('move_file_label', {
            defaultValue:
              'Verschieben: Öffnet Popup zum Auswählen des Zielordners für die Verschiebung',
          })}
          className="fileListRowContextMenuBtn"
          key="btnMove"
          appearance="transparent"
          onClick={runAndClose(() => onMoveFileClick([file]))}
        >
          {t('move', { defaultValue: 'Verschieben' })}
        </Button>,
      );
    }

    if (onCopyFileClick !== null) {
      menuItems.push(
        <Button
          aria-label={t('copy_file_label', {
            defaultValue: 'Kopieren: Öffnet Popup zum Auswählen des Zielordners für die Kopie',
          })}
          className="fileListRowContextMenuBtn"
          key="btnCopy"
          appearance="transparent"
          onClick={runAndClose(() => onCopyFileClick([file]))}
        >
          {t('copy', { defaultValue: 'Kopieren' })}
        </Button>,
      );
    }

    if (onShareFileClick !== null && !isFolder(file)) {
      menuItems.push(
        <Button
          aria-label={t('files:fileslist:share_link_label', {
            defaultValue: 'Link freigeben: Öffnet Popup zum Freigeben der Datei',
          })}
          className="fileListRowContextMenuBtn"
          key="btnShareLink"
          appearance="transparent"
          onClick={runAndClose(() => onShareFileClick(file))}
        >
          {t('share_link', { defaultValue: 'Link freigeben' })}
        </Button>,
      );
    }

    if (onDownloadAsPdfClick !== null && isDownloadableAsPdf(file)) {
      menuItems.push(
        <Button
          className="fileListRowContextMenuBtn"
          key="btnDownloadPdf"
          appearance="transparent"
          onClick={runAndClose(() => onDownloadAsPdfClick(file))}
        >
          {t('download_as_pdf', { defaultValue: 'Als PDF herunterladen' })}
        </Button>,
      );
    }

    if (onRenameFileClick !== null) {
      menuItems.push(
        <Button
          aria-label={t('rename_file_label', {
            defaultValue: 'Umbenennen: Öffnet Popup zum Umbenennen der Datei',
          })}
          className="fileListRowContextMenuBtn"
          key="btnRename"
          appearance="transparent"
          data-cy="fileRenameButton"
          onClick={runAndClose(() => onRenameFileClick(file))}
        >
          {t('rename', { defaultValue: 'Umbenennen' })}
        </Button>,
      );
    }

    if (onDeleteFileClick !== null) {
      menuItems.push(
        <Button
          className="fileListRowContextMenuBtn"
          key="btnDelete"
          appearance="transparent"
          onClick={runAndClose(() => onDeleteFileClick(file))}
        >
          {t('delete', { defaultValue: 'Löschen' })}
        </Button>,
      );
    }

    return menuItems;
  }, [
    onOpenFileClick,
    file,
    onDownloadFileClick,
    onDownloadZipClick,
    onPreviewFileClick,
    onMoveFileClick,
    onCopyFileClick,
    onShareFileClick,
    onDownloadAsPdfClick,
    onRenameFileClick,
    onDeleteFileClick,
    t,
  ]);

  return (
    <li
      key={file.id}
      className={cn('filesListRow', 'folder')}
      data-testid={`filesListRow-${file.name}`}
    >
      <span className="filesListColCheckbox">
        {isSelectable(file, allowFolderSelection, allowFileSelection) ? (
          <Checkbox
            checked={isSelected}
            onChange={handleCheckChange}
            aria-label={
              isSelected
                ? t('file_deselect_label', {
                    defaultValue: 'Auswahl von {{fileName}} aufheben',
                    fileName: file.name || '',
                  })
                : t('file_select_label', {
                    defaultValue: '{{fileName}} auswählen',
                    fileName: file.name || '',
                  })
            }
          />
        ) : (
          ''
        )}
      </span>
      <span
        tabIndex={0}
        role="button"
        className="filesListColIcon"
        aria-label={t('file_open_label', {
          defaultValue: '{{fileName}} öffnen',
          fileName: file.name || '',
        })}
        onClick={handleClick}
        onKeyDown={handleClick}
      >
        {getFileListRowIcon(file)}
      </span>
      <span className="filesListColFilename" data-testid="colFilename">
        {isRecentFile(file) && recentFileIndicator(t)}
        <span
          tabIndex={0}
          role="button"
          className="filename"
          onClick={handleClick}
          data-testid="filename"
          onKeyDown={handleClick}
        >
          {file.name}
        </span>
      </span>
      <span className="filesListColMenuWrapper">
        {contextMenuItems.length > 0 && (
          <Popup
            open={menuOpen}
            onOpenChange={setMenuOpen}
            trigger={
              <Button
                appearance="transparent"
                icon={<MoreVerticalFilled />}
                aria-label={t('more_actions_label', {
                  defaultValue: 'Öffnet Kontextmenü für weitere Aktionen für die ausgewählte Datei',
                })}
                className="menuIcon"
                data-testid="moreIcon"
              />
            }
            position="below"
            content={<div className="flex flex-col items-start">{contextMenuItems}</div>}
          />
        )}
      </span>
      <span className="filesListColFilesize" data-testid="colFileSize">
        {file.size === undefined ? '' : formatBytes(file.size)}
      </span>
      <span className="filesListColLastModifiedBy" data-testid="colLastModifiedBy">
        {get(file, 'lastModifiedBy.user.displayName')}
      </span>
      <span className="filesListColLastModifiedDate">
        {file.lastModifiedDateTime
          ? moment(file.lastModifiedDateTime).format('DD.MM.YY HH:mm')
          : ''}
      </span>
    </li>
  );
};
